import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, User, Building2, Mail, Phone, Globe, Tag, AlertTriangle } from 'lucide-react';
import { Spinner } from '@centraoneplus/ui';
import { customerService, CreateCustomerDto, Customer } from '@services/customer.service';
import { COUNTRY_CODE_REQUIRED_MESSAGE, combinePhone } from '@/lib/phone';
import { CountryCodeSelect } from '@components/CountryCodeSelect';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ThemedSelect } from '@components/common/ThemedSelect';

// A potential duplicate of the customer being created, with the identifier(s)
// (email / name / phone) that matched an existing record.
interface DuplicateMatch {
  customer: Customer;
  reasons: string[];
}

export default function CreateCustomerPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState('');

  const [formData, setFormData] = useState<CreateCustomerDto>({
    email: '',
    name: '',
    phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    tags: [],
    customFields: {},
  });

  const [tagInput, setTagInput] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // Surface potential duplicates while the agent types so a parallel record
  // isn't silently created (US #8695 AC2). Debounced; checks each filled
  // identifier (email / name / phone) against existing customers and keeps only
  // exact matches (the search API does substring OR-matching, which would
  // otherwise produce false positives).
  const emailValue = formData.email;
  const nameValue = formData.name;
  const phoneValue = formData.phone;
  useEffect(() => {
    const normEmail = (emailValue || '').trim().toLowerCase();
    const normName = (nameValue || '').trim().toLowerCase();
    const normPhone = (phoneValue || '').replace(/\D/g, '');

    const checks: Array<{ reason: string; value: string; limit?: number; match: (c: Customer) => boolean }> = [];
    if (normEmail.includes('@')) {
      checks.push({ reason: 'email', value: normEmail, match: (c) => (c.email || '').trim().toLowerCase() === normEmail });
    }
    if (normName.length >= 2) {
      checks.push({ reason: 'name', value: normName, match: (c) => (c.name || '').trim().toLowerCase() === normName });
    }
    if (normPhone.length === 10) {
      // Match on the last 10 digits so formatting/country code on either side is
      // ignored (e.g. entered "+1 555-123-4567" vs stored "5551234567").
      //
      // Search by the LAST 4 digits, not the full number: the list API does a
      // raw substring match on the stored phone, so a stored value with
      // separators ("555-123-4567") would never contain the full "5551234567"
      // and the record would not come back at all. The trailing 4 digits survive
      // common grouping, so the record is returned; the exact last-10 match below
      // then filters out any incidental hits. Wider limit for the same reason.
      checks.push({
        reason: 'phone',
        value: normPhone.slice(-4),
        limit: 50,
        match: (c) => (c.phone || '').replace(/\D/g, '').slice(-10) === normPhone,
      });
    }

    if (checks.length === 0) {
      setDuplicates([]);
      setCheckingDuplicates(false);
      return;
    }

    let cancelled = false;
    setCheckingDuplicates(true);
    const timer = setTimeout(async () => {
      try {
        const results = await Promise.all(
          checks.map((c) => customerService.getCustomers({ search: c.value, limit: c.limit ?? 5 })),
        );
        if (cancelled) return;

        // Aggregate matches by customer id, collecting which identifier matched.
        const byId = new Map<string, DuplicateMatch>();
        checks.forEach((check, i) => {
          for (const c of results[i].data) {
            if (c.status === 'merged' || !check.match(c)) continue;
            const existing = byId.get(c.id);
            if (existing) {
              if (!existing.reasons.includes(check.reason)) existing.reasons.push(check.reason);
            } else {
              byId.set(c.id, { customer: c, reasons: [check.reason] });
            }
          }
        });
        setDuplicates(Array.from(byId.values()));
      } catch {
        // Duplicate detection is advisory — never block creation on a failed check.
        if (!cancelled) setDuplicates([]);
      } finally {
        if (!cancelled) setCheckingDuplicates(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailValue, nameValue, phoneValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Keep the last 10 digits. Typing is already capped at 10 by maxLength, but
    // a paste can bypass that — e.g. "+1 555 123 4567" arrives as 11 digits.
    // Taking the trailing 10 drops the country/trunk prefix so the national
    // number is correct and the duplicate check matches the stored record
    // (which compares on the last 10 digits).
    const digits = e.target.value.replace(/\D/g, '').slice(-10);
    setFormData((prev) => ({ ...prev, phone: digits }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((t) => t !== tag) || [],
    }));
  };

  // Block submission when the inline check found an existing customer that
  // shares this email OR phone — both are enforced as unique by the backend
  // (email via the DB unique constraint, phone via Bug 8848's 409 guard), so
  // creating would fail anyway. A duplicate NAME stays advisory only.
  const blockingReasons = Array.from(
    new Set(
      duplicates
        .flatMap((d) => d.reasons)
        .filter((r) => r === 'email' || r === 'phone'),
    ),
  );
  const hasBlockingDuplicate = blockingReasons.length > 0;
  // e.g. "email", "phone number", or "email and phone number".
  const blockingLabel = blockingReasons
    .map((r) => (r === 'phone' ? 'phone number' : r))
    .join(' and ');
  const blockingMessage = hasBlockingDuplicate
    ? `A customer with this ${blockingLabel} already exists. Please use a unique email or phone number.`
    : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email) {
      setError('Email is required');
      return;
    }

    if (hasBlockingDuplicate) {
      setError(blockingMessage);
      return;
    }

    if (formData.phone && formData.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      return;
    }

    if (formData.phone && !countryCode) {
      setError(COUNTRY_CODE_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    try {
      // Store the phone with its country code, e.g. "+1 5551234567".
      const customer = await customerService.createCustomer({
        ...formData,
        phone: combinePhone(countryCode, formData.phone || ''),
      });
      navigate(`/customers/${customer.id}`);
    } catch (err: any) {
      console.error('Failed to create customer:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header — floating card, row 1 only */}
      <div
        className="flex items-center justify-between gap-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-4"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        <div className="flex min-w-0 items-center gap-4">
        <Link
          to="/customers"
          className="p-2 hover:bg-[var(--co-selected)] rounded-[10px] transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-[var(--co-ink-3)]" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[var(--co-ink)]">Add New Customer</h1>
          <p className="mt-1 text-sm text-[var(--co-ink-3)]">
            Create a new customer profile
          </p>
        </div>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Potential duplicate warning */}
      {duplicates.length > 0 && (
        <div className="card p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Potential duplicate{duplicates.length > 1 ? 's' : ''} found
              </p>
              <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-300">
                {duplicates.map(({ customer, reasons }) => (
                  <li key={customer.id}>
                    Customer{' '}
                    <Link
                      to={`/customers/${customer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium underline hover:text-amber-900 dark:hover:text-amber-200"
                    >
                      {customer.name || customer.email}
                    </Link>{' '}
                    already uses this {reasons.join(' and ')}.
                  </li>
                ))}
              </ul>
              {hasBlockingDuplicate ? (
                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                  {blockingMessage}
                </p>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Review the existing record before creating a new one to avoid splitting their history.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {checkingDuplicates && duplicates.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <Spinner size="sm" /> Checking for existing customers…
        </p>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <User className="h-5 w-5" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="customer@example.com"
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number
              </label>
              <div className="flex gap-2">
                <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="5551234567"
                    className="input pl-10 w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Localization
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone
              </label>
              <ThemedSelect
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="input w-full min-h-[38px]"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Australia/Sydney">Sydney (AEDT)</option>
              </ThemedSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language
              </label>
              <ThemedSelect
                name="locale"
                value={formData.locale}
                onChange={handleChange}
                className="input w-full min-h-[38px]"
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="es-ES">Spanish</option>
                <option value="fr-FR">French</option>
                <option value="de-DE">German</option>
                <option value="it-IT">Italian</option>
                <option value="pt-BR">Portuguese (Brazil)</option>
                <option value="ja-JP">Japanese</option>
                <option value="zh-CN">Chinese (Simplified)</option>
                <option value="hi-IN">Hindi</option>
              </ThemedSelect>
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Tags
          </h2>

          <div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="input flex-1"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="btn btn-outline"
              >
                Add
              </button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-500 transition-colors"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Additional Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                External ID
              </label>
              <input
                type="text"
                name="externalId"
                value={formData.externalId || ''}
                onChange={handleChange}
                placeholder="ID from external system"
                disabled
                className="input w-full disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Source
              </label>
              <ThemedSelect
                name="source"
                value={formData.source || ''}
                onChange={handleChange}
                disabled
                className="input w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select source...</option>
                <option value="manual">Manual Entry</option>
                <option value="import">Import</option>
                <option value="api">API</option>
                <option value="chat">Chat Widget</option>
                <option value="signup">Website Signup</option>
                <option value="referral">Referral</option>
              </ThemedSelect>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/customers" className="btn btn-outline">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || hasBlockingDuplicate}
            title={hasBlockingDuplicate ? blockingMessage : undefined}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Create Customer
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
